import { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { putData, getData } from "../../API";
import { useFormik } from "formik";
import * as Yup from "yup";

function ModuleEdit({ editableDetails, setEditShow, fetchData, handleSearch }) {
  const [rateCategories, setRateCategories] = useState([]);
  const [rateSubCategories, setRateSubCategories] = useState([]);

  const validationSchema = Yup.object({
    rateCategoryId: Yup.number()
      .required("Category is required")
      .typeError("Category is required"),
    rateSubCategoryId: Yup.number()
      .required("Sub Category is required")
      .typeError("Sub Category is required"),
    fixedValue: Yup.string().required("Rate is required"),
  });

  const formik = useFormik({
    initialValues: {
      sdid: editableDetails.sdid,
      rateCategoryId: editableDetails.rateCategoryId || "",
      rateCategoryName: editableDetails.categoryName || "",
      rateSubCategoryId: editableDetails.rateSubCategoryId || "",
      rateSubCategoryName: editableDetails.rateSubCategoryName || "",
      fixedValue: editableDetails.fixedValue || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        const res = await putData("SurveyDetails/UpdateVerifiedRecords", {
          ...values,
          rateCategoryId: parseInt(values.rateCategoryId),
          rateSubCategoryId: parseInt(values.rateSubCategoryId),
        });

        switch (res.statusCode) {
          case 200:
            toast.success("Data updated successfully!", { autoClose: 5000 });
            setEditShow(false);
            fetchData();
            handleSearch();
            break;
          case 409:
            toast.error(res.message, { autoClose: 5000 });
            break;
          case 400:
              toast.error(res.message, { autoClose: 5000 });
              break;
          default:
            toast.error("Unexpected response: " + res.message, { autoClose: 5000 });
        }
      } catch (error) {
        toast.error("An error occurred while updating data", { autoClose: 5000 });
      }
    },
  });

  useEffect(() => {
    const fetchRateCategories = async () => {
      try {
        const res = await getData("RateCategory/GetCategories");
        const activeCategories = res?.items?.filter((item) => item.isActive) || [];
        setRateCategories(activeCategories);
      } catch (error) {
        toast.error("Failed to load rate categories", { autoClose: 5000 });
      }
    };
    fetchRateCategories();
  }, []);

  useEffect(() => {
    const fetchRateSubCategories = async () => {
      if (formik.values.rateCategoryId) {
        try {
          const res = await getData("RateSubCategory/GetSubCategories");
          const filtered = res?.items?.filter((item) => item.isActive) || [];
          setRateSubCategories(filtered);
        } catch (error) {
          toast.error("Failed to load rate subcategories", { autoClose: 5000 });
        }
      } else {
        setRateSubCategories([]);
      }
    };
    fetchRateSubCategories();
  }, [formik.values.rateCategoryId]);

  return (
    <Form onSubmit={formik.handleSubmit}>
      <div className="row filters ps-4 pt-3 pb-3 pe-4">
        <div className="col-md-6">
          <Form.Group controlId="rateCategoryId" className="mb-3">
            <Form.Label>Category<span className="text-danger">*</span></Form.Label>
            <Form.Select
              name="rateCategoryId"
              value={formik.values.rateCategoryId}
              onChange={(e) => {
                const selectedId = parseInt(e.target.value);
                const selected = rateCategories.find(
                  (item) => item.rtId === selectedId
                );
                formik.setValues({
                  ...formik.values,
                  rateCategoryId: selectedId,
                  rateCategoryName: selected?.categoryName || "",
                  rateSubCategoryId: "",
                  rateSubCategoryName: "",
                });
              }}
              onBlur={formik.handleBlur}
            >
              <option value="">Select Category</option>
              {rateCategories.map((cat) => (
                <option key={cat.rtId} value={cat.rtId}>
                  {cat.categoryName}
                </option>
              ))}
            </Form.Select>
            {formik.touched.rateCategoryId && formik.errors.rateCategoryId && (
              <div className="text-danger">{formik.errors.rateCategoryId}</div>
            )}
          </Form.Group>
        </div>

        <div className="col-md-6">
          <Form.Group controlId="rateSubCategoryId" className="mb-3">
            <Form.Label>Sub Category<span className="text-danger">*</span></Form.Label>
            <Form.Select
              name="rateSubCategoryId"
              value={formik.values.rateSubCategoryId}
              onChange={(e) => {
                const selectedId = parseInt(e.target.value);
                const selected = rateSubCategories.find(
                  (item) => item.rsctId === selectedId
                );
                formik.setValues({
                  ...formik.values,
                  rateSubCategoryId: selectedId,
                  rateSubCategoryName: selected?.subCategoryName || "",
                });
              }}
              onBlur={formik.handleBlur}
              disabled={!formik.values.rateCategoryId}
            >
              <option value="">Select Sub Category</option>
              {rateSubCategories.map((sub) => (
                <option key={sub.rsctId} value={sub.rsctId}>
                  {sub.subCategoryName}
                </option>
              ))}
            </Form.Select>
            {formik.touched.rateSubCategoryId && formik.errors.rateSubCategoryId && (
              <div className="text-danger">{formik.errors.rateSubCategoryId}</div>
            )}
          </Form.Group>
        </div>

        <div className="col-md-6">
          <Form.Group controlId="fixedValue" className="mb-3">
            <Form.Label>Rate<span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="fixedValue"
              value={formik.values.fixedValue}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter Rate"
            />
            {formik.touched.fixedValue && formik.errors.fixedValue && (
              <div className="text-danger">{formik.errors.fixedValue}</div>
            )}
          </Form.Group>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button variant="primary" type="submit">
          SUBMIT
        </Button>
      </div>
    </Form>
  );
}

export default ModuleEdit;
